import { setProfileNameInRouter } from '@app/_init/utils'
import { performRedirectsPostLogin } from '@app/_init/utils/redirections'
import { useStores, useLogger } from '@app/hooks'
import React from 'react'

/**
 * When being authenticated, setup the profileName and perform redirections.
 * At logout, the app is fully reloaded, so no need to handle it here.
 */
export function usePostLogin(): void {
  const {
    storeAuthentication,
    storeEula,
    storeLicense,
    storeAbout,
    storeReleaseNotes,
    storeRoot
  } = useStores()

  const logger = useLogger()

  React.useEffect(() => {
    // Set profile name in router before performing redirections
    setProfileNameInRouter(storeAuthentication)

    logger.debug('[performRedirectsPostLogin]')
    performRedirectsPostLogin(
      storeAuthentication,
      storeEula,
      storeLicense,
      storeAbout,
      storeReleaseNotes,
      storeRoot,
      document.location.pathname
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // trigger when being authenticated
    storeAuthentication.isAuthenticated,
    // or when EULA is approved,
    storeEula.hasApprovedEula,
    // trigger whenever licenseStatus is updated
    storeLicense.isLicenseStatusAuthorizeAccess,
    // trigger whenever the current profile is defined
    storeAuthentication.currentProfile
  ])

  // refresh license every X for the banner.
  useLicensePeriodicRefresh()
}

/**
 * Fetch license periodically to ensure that the banner message remains
 * accurate when the number of active users is updated.
 */
function useLicensePeriodicRefresh() {
  const logger = useLogger()

  const { storeAuthentication, storeLicense } = useStores()

  React.useEffect(() => {
    logger.debug('[loadLicense]')

    if (!storeAuthentication.isAuthenticated) {
      return
    }

    // Flag to stop execution when unmounted
    let isMounted = true

    function fetchLicense() {
      // Stop execution if component is unmounted
      if (!isMounted) {
        return
      }

      storeLicense.fetchLicense()

      setTimeout(() => {
        fetchLicense()
      }, 1000 * 60) // Fetch every 60 seconds
    }

    fetchLicense()

    return () => {
      isMounted = false // Cleanup to prevent future executions
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
